import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { ZInput, ZTextarea, ZSelect } from "@/components/ZInput";
import ZButton from "@/components/ZButton";
import Modal from "@/components/Modal";
import { fileToBase64, CURRENCIES, formatAmount } from "@/lib/format";
import { Plus, Trash2, Percent, Wallet, CheckCircle2, Ticket as TicketIcon, Gift } from "lucide-react";

const CATEGORIES = ["Concert", "Festival", "Conférence", "Privé"];

const emptyCat = () => ({ name: "", description: "", price: 0, capacity: 50, sale_starts_at: "", sale_ends_at: "" });

// Mirror of backend _free_fee_for_capacity: pick the fee tier matching the capacity.
function feeForCapacity(settings, capacity) {
  if (!settings) return { fee: 0, currency: "GNF" };
  const curr = settings.free_event_fee_currency || "GNF";
  const tiers = [...(settings.free_event_fee_tiers || [])].sort(
    (a, b) => (Number(a.max_attendees) === 0 ? 1 : 0) - (Number(b.max_attendees) === 0 ? 1 : 0) || Number(a.max_attendees) - Number(b.max_attendees)
  );
  for (const t of tiers) {
    const m = Number(t.max_attendees);
    if (m === 0) return { fee: Number(t.fee), currency: curr };
    if (capacity <= m) return { fee: Number(t.fee), currency: curr };
  }
  return { fee: Number(settings.free_event_fee || 0), currency: curr };
}

export default function OrgCreatePage() {
  const { id } = useParams();
  const editing = !!id;
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", description: "", category: "Concert", location: "",
    starts_at: "", image_url: "", is_private: false, price: 0, capacity: 100, currency: "GNF",
  });
  const [billingType, setBillingType] = useState("paid"); // 'paid' | 'free'
  const [settings, setSettings] = useState(null);
  const [forfaitEvent, setForfaitEvent] = useState(null); // created free event awaiting payment
  const [paying, setPaying] = useState(false);
  const [cats, setCats] = useState([]);  // ticket categories
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);

  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editing) return;
    api.get(`/events/${id}`).then((r) => {
      const e = r.data;
      const d = new Date(e.starts_at);
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setForm({
        title: e.title, description: e.description, category: e.category, location: e.location,
        starts_at: local, image_url: e.image_url, is_private: e.is_private, price: e.price, capacity: e.capacity,
        currency: e.currency || "GNF",
      });
      setBillingType(e.is_paid_event ? "paid" : "free");
    });
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImgLoading(true);
    try {
      const b64 = await fileToBase64(f);
      set("image_url", b64);
    } finally {
      setImgLoading(false);
    }
  };

  const updateCat = (i, k, v) => setCats((arr) => arr.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const addCat = () => setCats((arr) => [...arr, emptyCat()]);
  const removeCat = (i) => setCats((arr) => arr.filter((_, idx) => idx !== i));

  const isFree = billingType === "free";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.title.trim()) return setErr("Titre obligatoire.");
    if (!form.starts_at) return setErr("Date de l'événement obligatoire.");
    // Free events: ignore ticket categories & price entirely.
    const useFreeMode = isFree;
    // Validate categories if present (paid only)
    const cleanCats = useFreeMode ? [] : cats.map((c) => ({
      name: c.name.trim(),
      description: (c.description || "").trim(),
      price: parseFloat(c.price) || 0,
      capacity: parseInt(c.capacity) || 0,
      sale_starts_at: c.sale_starts_at ? new Date(c.sale_starts_at).toISOString() : null,
      sale_ends_at: c.sale_ends_at ? new Date(c.sale_ends_at).toISOString() : null,
    }));
    if (!editing && cleanCats.length > 0) {
      for (const c of cleanCats) {
        if (!c.name) return setErr("Chaque catégorie doit avoir un nom.");
        if (c.capacity < 1) return setErr(`Capacité invalide pour la catégorie "${c.name}".`);
      }
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        location: form.location.trim(),
        starts_at: new Date(form.starts_at).toISOString(),
        price: useFreeMode ? 0 : (parseFloat(form.price) || 0),
        capacity: parseInt(form.capacity) || 0,
      };
      if (!editing) {
        payload.categories = cleanCats.map((c) => {
          const out = { name: c.name, description: c.description, price: c.price, capacity: c.capacity };
          if (c.sale_starts_at) out.sale_starts_at = c.sale_starts_at;
          if (c.sale_ends_at) out.sale_ends_at = c.sale_ends_at;
          return out;
        });
      }
      const res = editing
        ? await api.put(`/events/${id}`, payload)
        : await api.post("/events", payload);
      // Free event awaiting forfait payment → open the (mock) payment step.
      if (!editing && res.data.status === "pending_payment" && !res.data.forfait_paid) {
        setForfaitEvent(res.data);
        return;
      }
      nav(`/org/event/${res.data.id}`);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const payForfait = async () => {
    if (!forfaitEvent) return;
    setPaying(true);
    try {
      await api.post(`/events/${forfaitEvent.id}/pay-forfait`);
      nav(`/org/event/${forfaitEvent.id}`);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setPaying(false);
    }
  };

  const useCategories = !editing && !isFree && cats.length > 0;
  const previewCapacity = parseInt(form.capacity) || 0;
  const forfait = feeForCapacity(settings, previewCapacity);
  const commissionPct = settings?.commission_pct ?? 8;

  return (
    <div className="z-container" style={{ maxWidth: 720 }} data-testid="org-create-page">
      <h1 className="z-page-title">{editing ? "Modifier l'événement" : "Nouvel événement"}</h1>
      <p className="z-page-sub">Renseignez les détails ci-dessous.</p>

      <form onSubmit={submit} className="z-card" style={{ padding: 24 }}>
        <ZInput label="Titre" value={form.title} onChange={(e) => set("title", e.target.value)} required data-testid="event-form-title" />
        <ZTextarea label="Description" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="event-form-description" />
        <ZSelect label="Catégorie" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="event-form-category">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </ZSelect>
        <ZInput label="Lieu" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Dakar, Sénégal" data-testid="event-form-location" />
        <ZInput label="Date et heure" type="datetime-local" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} required data-testid="event-form-date" />

        {/* ----- Type de billetterie : Gratuit vs Payant ----- */}
        <label className="z-label" style={{ marginTop: 6 }}>Type de billetterie</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          <button
            type="button"
            onClick={() => setBillingType("paid")}
            data-testid="billing-type-paid"
            className="z-card"
            style={{
              padding: 14, textAlign: "left", cursor: "pointer", border: "2px solid",
              borderColor: billingType === "paid" ? "var(--z-teal)" : "var(--z-border,#e5e5e5)",
              background: billingType === "paid" ? "rgba(1,105,111,0.06)" : "#fff",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}><TicketIcon size={16} /> Payant</div>
            <div style={{ fontSize: 12.5, color: "var(--z-muted)", marginTop: 4 }}>
              Vous vendez des billets. La plateforme prélève une commission sur les ventes.
            </div>
          </button>
          <button
            type="button"
            onClick={() => setBillingType("free")}
            data-testid="billing-type-free"
            className="z-card"
            style={{
              padding: 14, textAlign: "left", cursor: "pointer", border: "2px solid",
              borderColor: billingType === "free" ? "var(--z-orange)" : "var(--z-border,#e5e5e5)",
              background: billingType === "free" ? "rgba(218,113,1,0.06)" : "#fff",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}><Gift size={16} /> Gratuit</div>
            <div style={{ fontSize: 12.5, color: "var(--z-muted)", marginTop: 4 }}>
              Entrée gratuite. Un forfait fixe est dû avant la mise en ligne.
            </div>
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: (useCategories || isFree) ? "1fr 1fr" : "1fr 1fr 1fr", gap: 12 }}>
          {!useCategories && !isFree && (
            <ZInput label="Prix unitaire" type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="event-form-price" />
          )}
          <ZSelect label="Devise" value={form.currency} onChange={(e) => set("currency", e.target.value)} data-testid="event-form-currency">
            {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
          </ZSelect>
          <ZInput label={isFree ? "Personnes attendues" : "Capacité"} type="number" min="1" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} data-testid="event-form-capacity" />
        </div>

        {/* ----- Encart commission (payant) ----- */}
        {billingType === "paid" && (
          <div data-testid="commission-info" style={{ background: "rgba(1,105,111,0.07)", borderRadius: 12, padding: 14, marginBottom: 14, fontSize: 13.5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "var(--z-teal)" }}>
              <Percent size={16} /> Commission plateforme : {commissionPct}%
            </div>
            <p style={{ margin: "6px 0 0", color: "var(--z-muted)" }}>
              Sur chaque billet vendu, vous recevez {(100 - commissionPct).toFixed(0)}% du montant.
              {(parseFloat(form.price) > 0) && (
                <> Ex. : un billet à {formatAmount(parseFloat(form.price) || 0, form.currency)} → vous percevez{" "}
                  <strong>{formatAmount((parseFloat(form.price) || 0) * (1 - commissionPct / 100), form.currency)}</strong>.</>
              )}
            </p>
          </div>
        )}

        {/* ----- Encart forfait (gratuit) ----- */}
        {billingType === "free" && (
          <div data-testid="forfait-info" style={{ background: "rgba(218,113,1,0.08)", borderRadius: 12, padding: 14, marginBottom: 14, fontSize: 13.5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "var(--z-orange)" }}>
              <Wallet size={16} /> Forfait à régler : {formatAmount(forfait.fee, forfait.currency)}
            </div>
            <p style={{ margin: "6px 0 0", color: "var(--z-muted)" }}>
              Montant fixe pour un événement gratuit jusqu&apos;à <strong>{previewCapacity}</strong> personne(s).
              Le règlement (simulé) est demandé avant la soumission à la plateforme.
            </p>
          </div>
        )}

        {!editing && !isFree && (
          <>
            <div className="z-divider" />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <label className="z-label" style={{ marginBottom: 0 }}>
                Catégories de billets (VIP, Early Bird…)
              </label>
              <button type="button" className="z-btn z-btn-outline z-btn-sm" onClick={addCat} data-testid="add-category-btn">
                <Plus size={14} /> Ajouter
              </button>
            </div>
            <p className="z-help" style={{ marginTop: 0 }}>
              {cats.length === 0
                ? "Optionnel — sinon le prix/capacité ci-dessus s'applique à tous les billets."
                : "Le prix et la capacité ci-dessus sont ignorés au profit des catégories."}
            </p>
            {cats.map((c, i) => (
              <div key={i} className="z-card" style={{ padding: 14, marginBottom: 10, background: "#fafafa" }} data-testid={`category-row-${i}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <strong style={{ fontSize: 14 }}>Catégorie {i + 1}</strong>
                  <button type="button" onClick={() => removeCat(i)} className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error,#c00)" }} data-testid={`category-remove-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10 }}>
                  <ZInput label="Nom" value={c.name} onChange={(e) => updateCat(i, "name", e.target.value)} placeholder="VIP, Standard…" data-testid={`category-name-${i}`} />
                  <ZInput label="Prix" type="number" min="0" step="0.01" value={c.price} onChange={(e) => updateCat(i, "price", e.target.value)} data-testid={`category-price-${i}`} />
                  <ZInput label="Capacité" type="number" min="1" value={c.capacity} onChange={(e) => updateCat(i, "capacity", e.target.value)} data-testid={`category-capacity-${i}`} />
                </div>
                <ZInput label="Description (optionnel)" value={c.description} onChange={(e) => updateCat(i, "description", e.target.value)} data-testid={`category-desc-${i}`} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <ZInput label="Ouverture vente (optionnel)" type="datetime-local" value={c.sale_starts_at} onChange={(e) => updateCat(i, "sale_starts_at", e.target.value)} data-testid={`category-start-${i}`} />
                  <ZInput label="Fin vente (Early Bird)" type="datetime-local" value={c.sale_ends_at} onChange={(e) => updateCat(i, "sale_ends_at", e.target.value)} data-testid={`category-end-${i}`} />
                </div>
              </div>
            ))}
          </>
        )}

        <label className="z-label">Image de couverture</label>
        <div style={{ marginBottom: 14 }}>
          <input type="file" accept="image/*" onChange={onFile} data-testid="event-form-image-file" style={{ marginBottom: 8 }} />
          {imgLoading && <span style={{ fontSize: 13, color: "var(--z-muted)" }}>Chargement…</span>}
          {form.image_url && (
            <div style={{ marginTop: 8 }}>
              <img src={form.image_url} alt="Aperçu" style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 12 }} />
            </div>
          )}
          <p className="z-help">Ou utilisez une URL d&apos;image :</p>
          <input className="z-input" value={form.image_url?.startsWith("data:") ? "" : form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://..." data-testid="event-form-image-url" />
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={form.is_private} onChange={(e) => set("is_private", e.target.checked)} data-testid="event-form-private" style={{ accentColor: "var(--z-teal)" }} />
          Événement privé (sur invitation uniquement)
        </label>

        {err && <p className="z-error-text" data-testid="event-form-error">{err}</p>}

        <ZButton type="submit" block loading={loading} data-testid="event-form-submit-btn">
          {editing ? "Enregistrer les modifications" : (isFree ? "Continuer vers le paiement du forfait" : "Créer l'événement")}
        </ZButton>
      </form>

      <Modal open={!!forfaitEvent} onClose={() => setForfaitEvent(null)} title="Paiement du forfait" testId="forfait-modal">
        <p style={{ color: "var(--z-muted)", marginBottom: 12, fontSize: 14 }}>
          Votre événement gratuit <strong>{forfaitEvent?.title}</strong> sera soumis à la plateforme une fois le forfait réglé.
        </p>
        <div style={{ background: "rgba(218,113,1,0.08)", borderRadius: 12, padding: 16, textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: "var(--z-muted)" }}>Montant du forfait</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "var(--z-orange)" }} data-testid="forfait-amount">
            {forfaitEvent && formatAmount(forfaitEvent.forfait_amount, forfaitEvent.forfait_currency)}
          </div>
        </div>
        <p style={{ fontSize: 12, color: "var(--z-muted)", marginBottom: 14 }}>
          ⚠️ Paiement <strong>simulé</strong> pour le moment (le paiement réel sera bientôt disponible).
        </p>
        <ZButton block loading={paying} onClick={payForfait} data-testid="pay-forfait-btn">
          <CheckCircle2 size={16} /> Payer le forfait (simulé)
        </ZButton>
      </Modal>
    </div>
  );
}
